import { copyFileSync, existsSync, constants as fsConstants, mkdirSync, unlinkSync } from "node:fs";
import { basename, join, resolve } from "node:path";
import { SessionManager } from "@caupulican/pi-agent-core/session";
import { resolvePath } from "../utils/paths.ts";
import type { AgentSession } from "./agent-session.ts";
import type { AgentSessionRuntimeDiagnostic, AgentSessionServices } from "./agent-session-services.ts";
import type { ReplacedSessionContext, SessionShutdownEvent, SessionStartEvent } from "./extensions/index.ts";
import { emitSessionShutdownEvent } from "./extensions/runner.ts";
import type { CreateAgentSessionResult } from "./sdk.ts";
import { assertSessionCwdExists } from "./session-cwd.ts";

/**
 * Result returned by runtime creation.
 *
 * The caller gets the created session, its cwd-bound services, and all
 * diagnostics collected during setup.
 */
export interface CreateAgentSessionRuntimeResult extends CreateAgentSessionResult {
	services: AgentSessionServices;
	diagnostics: AgentSessionRuntimeDiagnostic[];
}

interface AgentSessionRuntimeState {
	session: AgentSession;
	services: AgentSessionServices;
	diagnostics: AgentSessionRuntimeDiagnostic[];
	modelFallbackMessage?: string;
}

interface RuntimeShutdownProgress {
	shutdownEventEmitted: boolean;
	stoppedResources: Set<AgentSessionRuntimeResource>;
	hostInvalidated: boolean;
	sessionDisposed: boolean;
}

/** Session-owned background runtime that must not survive a session replacement. */
export interface AgentSessionRuntimeResource {
	start(session: AgentSession): Promise<void>;
	stop(): Promise<void> | void;
}

/** Internal activation hooks used by the interactive session-resume flow. */
export interface AgentSessionSwitchOptions {
	cwdOverride?: string;
	/** Runs against the installed replacement after host rebind and before supervisors restart. */
	beforeSessionResourcesStart?: (session: AgentSession) => Promise<void> | void;
	withSession?: (ctx: ReplacedSessionContext) => Promise<void>;
}

interface SessionReplacementCommitOptions {
	reason: SessionShutdownEvent["reason"];
	targetSessionFile: string | undefined;
	beforeSessionResourcesStart?: (session: AgentSession) => Promise<void> | void;
	withSession?: (ctx: ReplacedSessionContext) => Promise<void>;
	onDiscard?: () => void;
}

/**
 * Creates a full runtime for a target cwd and session manager.
 *
 * The factory closes over process-global fixed inputs, recreates cwd-bound
 * services for the effective cwd, resolves session options against those
 * services, and finally creates the AgentSession.
 */
export type CreateAgentSessionRuntimeFactory = (options: {
	cwd: string;
	agentDir: string;
	sessionManager: SessionManager;
	sessionStartEvent?: SessionStartEvent;
}) => Promise<CreateAgentSessionRuntimeResult>;

/**
 * Thrown when /import references a JSONL file path that does not exist.
 */
export class SessionImportFileNotFoundError extends Error {
	readonly filePath: string;

	constructor(filePath: string) {
		super(`File not found: ${filePath}`);
		this.name = "SessionImportFileNotFoundError";
		this.filePath = filePath;
	}
}

/** A teardown or activation failure at the replacement boundary, including rollback outcome. */
export class SessionReplacementRuntimeError extends Error {
	readonly phase: "teardown" | "activation";
	readonly recovered: boolean;
	readonly transitionError: unknown;
	readonly recoveryError: unknown | undefined;

	constructor(
		phase: "teardown" | "activation",
		transitionError: unknown,
		recovered: boolean,
		recoveryError?: unknown,
	) {
		const transitionMessage = transitionError instanceof Error ? transitionError.message : String(transitionError);
		super(
			recovered
				? `Session replacement ${phase} failed; the previous session was restored: ${transitionMessage}`
				: `Session replacement ${phase} failed and the previous session could not be restored safely: ${transitionMessage}`,
		);
		this.name = "SessionReplacementRuntimeError";
		this.phase = phase;
		this.recovered = recovered;
		this.transitionError = transitionError;
		this.recoveryError = recoveryError;
	}
}

/** A post-commit extension callback failed; the replacement itself remains the active session. */
export class SessionReplacementCallbackError extends Error {
	readonly replacementCommitted = true;
	readonly callbackError: unknown;

	constructor(callbackError: unknown) {
		const callbackMessage = callbackError instanceof Error ? callbackError.message : String(callbackError);
		super(`Session replacement completed, but its withSession callback failed: ${callbackMessage}`);
		this.name = "SessionReplacementCallbackError";
		this.callbackError = callbackError;
	}
}

function extractUserMessageText(content: string | Array<{ type: string; text?: string }>): string {
	if (typeof content === "string") {
		return content;
	}

	return content
		.filter((part): part is { type: "text"; text: string } => part.type === "text" && typeof part.text === "string")
		.map((part) => part.text)
		.join("");
}

/**
 * Owns the current AgentSession plus its cwd-bound services.
 *
 * Session replacement methods construct the candidate runtime before retiring
 * the current one. If preparation fails, the working session remains active.
 */
export class AgentSessionRuntime {
	private rebindSession?: (session: AgentSession) => Promise<void>;
	private beforeSessionInvalidate?: () => void;
	private _session: AgentSession;
	private _services: AgentSessionServices;
	private readonly createRuntime: CreateAgentSessionRuntimeFactory;
	private _diagnostics: AgentSessionRuntimeDiagnostic[];
	private _modelFallbackMessage?: string;
	private _replacementInFlight: Promise<unknown> | undefined;
	private _disposeInFlight: Promise<void> | undefined;
	private _disposeStarted = false;
	private _disposed = false;
	private readonly disposeProgress: RuntimeShutdownProgress = {
		shutdownEventEmitted: false,
		stoppedResources: new Set(),
		hostInvalidated: false,
		sessionDisposed: false,
	};
	private readonly sessionResources: AgentSessionRuntimeResource[] = [];

	constructor(
		_session: AgentSession,
		_services: AgentSessionServices,
		createRuntime: CreateAgentSessionRuntimeFactory,
		_diagnostics: AgentSessionRuntimeDiagnostic[] = [],
		_modelFallbackMessage?: string,
	) {
		this._session = _session;
		this._services = _services;
		this.createRuntime = createRuntime;
		this._diagnostics = _diagnostics;
		this._modelFallbackMessage = _modelFallbackMessage;
	}

	get services(): AgentSessionServices {
		return this._services;
	}

	get session(): AgentSession {
		return this._session;
	}

	get cwd(): string {
		return this._services.cwd;
	}

	get diagnostics(): readonly AgentSessionRuntimeDiagnostic[] {
		return this._diagnostics;
	}

	get modelFallbackMessage(): string | undefined {
		return this._modelFallbackMessage;
	}

	/** True from replacement admission until rebind and replacement callbacks have settled. */
	get isReplacing(): boolean {
		return this._replacementInFlight !== undefined;
	}

	setRebindSession(rebindSession?: (session: AgentSession) => Promise<void>): void {
		this.rebindSession = rebindSession;
	}

	/**
	 * Set a synchronous callback that runs after `session_shutdown` handlers finish
	 * but before the current session is invalidated.
	 *
	 * This is for host-owned UI teardown that must not yield to the event loop,
	 * such as detaching extension-provided TUI components before the old extension
	 * context becomes stale.
	 */
	setBeforeSessionInvalidate(beforeSessionInvalidate?: () => void): void {
		this.beforeSessionInvalidate = beforeSessionInvalidate;
	}

	/** Register a resource whose lifecycle follows the active session (supervisors, watchers). */
	registerSessionResource(resource: AgentSessionRuntimeResource): void {
		if (this.sessionResources.includes(resource)) return;
		this.sessionResources.push(resource);
	}

	private async emitBeforeSwitch(
		reason: "new" | "resume",
		targetSessionFile?: string,
	): Promise<{ cancelled: boolean }> {
		const runner = this.session.extensionRunner;
		if (!runner.hasHandlers("session_before_switch")) {
			return { cancelled: false };
		}

		const result = await runner.emit({
			type: "session_before_switch",
			reason,
			targetSessionFile,
		});
		return { cancelled: result?.cancel === true };
	}

	private async emitBeforeFork(
		entryId: string,
		options: { position: "before" | "at" },
	): Promise<{ cancelled: boolean }> {
		const runner = this.session.extensionRunner;
		if (!runner.hasHandlers("session_before_fork")) {
			return { cancelled: false };
		}

		const result = await runner.emit({
			type: "session_before_fork",
			entryId,
			...options,
		});
		return { cancelled: result?.cancel === true };
	}

	private async teardownCurrent(reason: SessionShutdownEvent["reason"], targetSessionFile?: string): Promise<void> {
		const errors = await this.shutdownRuntime(this.session, reason, targetSessionFile, {
			stopResources: true,
			invalidateHost: true,
		});
		if (errors.length === 1) throw errors[0];
		if (errors.length > 1) throw new AggregateError(errors, "Session runtime teardown failed.");
	}

	private async discardPrepared(result: CreateAgentSessionRuntimeResult): Promise<void> {
		await result.session.disposeAndWait();
	}

	private removeCreatedSessionFile(sessionFile: string | undefined): void {
		if (!sessionFile || sessionFile === this.session.sessionFile || !existsSync(sessionFile)) return;
		try {
			unlinkSync(sessionFile);
		} catch {
			// Best-effort rollback of an artifact created solely for the rejected replacement.
		}
	}

	private async prepareReplacement(
		sessionManager: SessionManager,
		sessionStartEvent: SessionStartEvent,
	): Promise<CreateAgentSessionRuntimeResult> {
		return this.createRuntime({
			cwd: sessionManager.getCwd(),
			agentDir: this.services.agentDir,
			sessionManager,
			sessionStartEvent,
		});
	}

	private async commitReplacement(
		result: CreateAgentSessionRuntimeResult,
		options: SessionReplacementCommitOptions,
	): Promise<void> {
		const previous = this.snapshot();
		try {
			await this.teardownCurrent(options.reason, options.targetSessionFile);
		} catch (transitionError: unknown) {
			const recoveryError = await this.restorePreviousAfterRuntimeFailure(previous, result, options, false);
			throw new SessionReplacementRuntimeError(
				"teardown",
				transitionError,
				recoveryError === undefined,
				recoveryError,
			);
		}
		this.apply(result);
		try {
			await this.activateReplacement(options);
		} catch (activationError: unknown) {
			const recoveryError = await this.restorePreviousAfterRuntimeFailure(previous, result, options, true);
			throw new SessionReplacementRuntimeError(
				"activation",
				activationError,
				recoveryError === undefined,
				recoveryError,
			);
		}
		if (options.withSession) {
			try {
				await options.withSession(this.session.createReplacedSessionContext());
			} catch (callbackError: unknown) {
				throw new SessionReplacementCallbackError(callbackError);
			}
		}
	}

	private snapshot(): AgentSessionRuntimeState {
		return {
			session: this._session,
			services: this._services,
			diagnostics: [...this._diagnostics],
			...(this._modelFallbackMessage !== undefined ? { modelFallbackMessage: this._modelFallbackMessage } : {}),
		};
	}

	private apply(result: AgentSessionRuntimeState): void {
		this._session = result.session;
		this._services = result.services;
		this._diagnostics = result.diagnostics;
		this._modelFallbackMessage = result.modelFallbackMessage;
	}

	private async activateReplacement(options: SessionReplacementCommitOptions): Promise<void> {
		if (this.rebindSession) {
			await this.rebindSession(this.session);
		}
		await options.beforeSessionResourcesStart?.(this.session);
		for (const resource of this.sessionResources) await resource.start(this.session);
	}

	private async restorePreviousAfterRuntimeFailure(
		previous: AgentSessionRuntimeState,
		failed: CreateAgentSessionRuntimeResult,
		options: SessionReplacementCommitOptions,
		candidateActivated: boolean,
	): Promise<unknown | undefined> {
		const cleanupErrors = await this.shutdownRuntime(failed.session, options.reason, previous.session.sessionFile, {
			stopResources: candidateActivated,
			invalidateHost: candidateActivated,
		});
		// The old AgentSession was disposed at the commit boundary, but its SessionManager remains the
		// durable reconstruction source. Restore the old snapshot temporarily so rollback-only artifact
		// cleanup cannot mistake the failed candidate for the active session.
		this.apply(previous);
		try {
			options.onDiscard?.();
		} catch (error: unknown) {
			cleanupErrors.push(error);
		}

		let restored: CreateAgentSessionRuntimeResult | undefined;
		try {
			restored = await this.createRuntime({
				cwd: previous.services.cwd,
				agentDir: previous.services.agentDir,
				sessionManager: previous.session.sessionManager,
				sessionStartEvent: {
					type: "session_start",
					reason: "resume",
					previousSessionFile: failed.session.sessionFile,
				},
			});
			this.apply(restored);
			if (this.rebindSession) await this.rebindSession(this.session);
			for (const resource of this.sessionResources) await resource.start(this.session);
		} catch (recoveryError: unknown) {
			if (restored) {
				cleanupErrors.push(
					...(await this.shutdownRuntime(restored.session, "resume", failed.session.sessionFile, {
						stopResources: true,
						invalidateHost: true,
					})),
				);
			}
			this.apply(previous);
			return cleanupErrors.length > 0
				? new AggregateError([...cleanupErrors, recoveryError], "Session rollback and cleanup failed.")
				: recoveryError;
		}

		return cleanupErrors.length > 0
			? new AggregateError(
					cleanupErrors,
					"The previous session was restored, but failed replacement cleanup was incomplete.",
				)
			: undefined;
	}

	private async shutdownRuntime(
		session: AgentSession,
		reason: SessionShutdownEvent["reason"],
		targetSessionFile: string | undefined,
		options: { stopResources: boolean; invalidateHost: boolean; progress?: RuntimeShutdownProgress },
	): Promise<unknown[]> {
		const errors: unknown[] = [];
		const progress =
			options.progress ??
			({
				shutdownEventEmitted: false,
				stoppedResources: new Set(),
				hostInvalidated: false,
				sessionDisposed: false,
			} satisfies RuntimeShutdownProgress);
		if (!progress.shutdownEventEmitted) {
			try {
				await emitSessionShutdownEvent(session.extensionRunner, {
					type: "session_shutdown",
					reason,
					targetSessionFile,
				});
				progress.shutdownEventEmitted = true;
			} catch (error: unknown) {
				errors.push(error);
			}
		}
		if (options.stopResources) {
			for (const resource of this.sessionResources) {
				if (progress.stoppedResources.has(resource)) continue;
				try {
					await resource.stop();
					progress.stoppedResources.add(resource);
				} catch (error: unknown) {
					errors.push(error);
				}
			}
		}
		if (options.invalidateHost && !progress.hostInvalidated) {
			try {
				this.beforeSessionInvalidate?.();
				progress.hostInvalidated = true;
			} catch (error: unknown) {
				errors.push(error);
			}
		}
		if (!progress.sessionDisposed) {
			try {
				await session.disposeAndWait();
				progress.sessionDisposed = true;
			} catch (error: unknown) {
				errors.push(error);
			}
		}
		return errors;
	}

	private runSessionReplacement<T>(operation: () => Promise<T>): Promise<T> {
		if (this._disposeStarted) {
			return Promise.reject(new Error("The session runtime has already been disposed."));
		}
		if (this._replacementInFlight) {
			return Promise.reject(new Error("A session replacement is already in progress."));
		}
		const replacement = Promise.resolve().then(operation);
		this._replacementInFlight = replacement;
		return replacement.finally(() => {
			if (this._replacementInFlight === replacement) this._replacementInFlight = undefined;
		});
	}

	async switchSession(sessionPath: string, options?: AgentSessionSwitchOptions): Promise<{ cancelled: boolean }> {
		return this.runSessionReplacement(() => this.switchSessionUnserialized(sessionPath, options));
	}

	private async switchSessionUnserialized(
		sessionPath: string,
		options?: AgentSessionSwitchOptions,
	): Promise<{ cancelled: boolean }> {
		const beforeResult = await this.emitBeforeSwitch("resume", sessionPath);
		if (beforeResult.cancelled) {
			return beforeResult;
		}

		const previousSessionFile = this.session.sessionFile;
		const sessionManager = SessionManager.open(sessionPath, this.services.agentDir, undefined, options?.cwdOverride);
		assertSessionCwdExists(sessionManager, this.cwd);
		const prepared = await this.prepareReplacement(sessionManager, {
			type: "session_start",
			reason: "resume",
			previousSessionFile,
		});
		await this.commitReplacement(prepared, {
			reason: "resume",
			targetSessionFile: sessionManager.getSessionFile(),
			beforeSessionResourcesStart: options?.beforeSessionResourcesStart,
			withSession: options?.withSession,
		});
		return { cancelled: false };
	}

	async newSession(options?: {
		parentSession?: string;
		setup?: (sessionManager: SessionManager) => Promise<void>;
		withSession?: (ctx: ReplacedSessionContext) => Promise<void>;
	}): Promise<{ cancelled: boolean }> {
		return this.runSessionReplacement(() => this.newSessionUnserialized(options));
	}

	private async newSessionUnserialized(options?: {
		parentSession?: string;
		setup?: (sessionManager: SessionManager) => Promise<void>;
		withSession?: (ctx: ReplacedSessionContext) => Promise<void>;
	}): Promise<{ cancelled: boolean }> {
		const beforeResult = await this.emitBeforeSwitch("new");
		if (beforeResult.cancelled) {
			return beforeResult;
		}

		const previousSessionFile = this.session.sessionFile;
		const sessionDir = this.session.sessionManager.getSessionDir();
		const sessionManager = SessionManager.create(this.cwd, this.services.agentDir, sessionDir);
		if (options?.parentSession) {
			sessionManager.newSession({ parentSession: options.parentSession });
		}

		let prepared: CreateAgentSessionRuntimeResult;
		try {
			prepared = await this.prepareReplacement(sessionManager, {
				type: "session_start",
				reason: "new",
				previousSessionFile,
			});
		} catch (error) {
			this.removeCreatedSessionFile(sessionManager.getSessionFile());
			throw error;
		}
		if (options?.setup) {
			try {
				await options.setup(prepared.session.sessionManager);
				prepared.session.agent.state.messages = prepared.session.sessionManager.buildSessionContext().messages;
			} catch (error) {
				await this.discardPrepared(prepared);
				this.removeCreatedSessionFile(sessionManager.getSessionFile());
				throw error;
			}
		}
		await this.commitReplacement(prepared, {
			reason: "new",
			targetSessionFile: sessionManager.getSessionFile(),
			withSession: options?.withSession,
			onDiscard: () => this.removeCreatedSessionFile(sessionManager.getSessionFile()),
		});
		return { cancelled: false };
	}

	async fork(
		entryId: string,
		options?: { position?: "before" | "at"; withSession?: (ctx: ReplacedSessionContext) => Promise<void> },
	): Promise<{ cancelled: boolean; selectedText?: string }> {
		return this.runSessionReplacement(() => this.forkUnserialized(entryId, options));
	}

	private async forkUnserialized(
		entryId: string,
		options?: { position?: "before" | "at"; withSession?: (ctx: ReplacedSessionContext) => Promise<void> },
	): Promise<{ cancelled: boolean; selectedText?: string }> {
		const position = options?.position ?? "before";
		const beforeResult = await this.emitBeforeFork(entryId, { position });
		if (beforeResult.cancelled) {
			return { cancelled: true };
		}
		let targetLeafId: string | null;
		let selectedText: string | undefined;

		const selectedEntry = this.session.sessionManager.getEntry(entryId);
		if (!selectedEntry) {
			throw new Error("Invalid entry ID for forking");
		}

		if (position === "at") {
			targetLeafId = selectedEntry.id;
		} else {
			if (selectedEntry.type !== "message" || selectedEntry.message.role !== "user") {
				throw new Error("Invalid entry ID for forking");
			}
			targetLeafId = selectedEntry.parentId;
			selectedText = extractUserMessageText(selectedEntry.message.content);
		}

		const previousSessionFile = this.session.sessionFile;
		if (this.session.sessionManager.isPersisted()) {
			const currentSessionFile = this.session.sessionFile;
			if (!currentSessionFile) {
				throw new Error("Persisted session is missing a session file");
			}
			const sessionDir = this.session.sessionManager.getSessionDir();
			if (!targetLeafId) {
				const sessionManager = SessionManager.create(this.cwd, this.services.agentDir, sessionDir);
				sessionManager.newSession({ parentSession: currentSessionFile });
				let prepared: CreateAgentSessionRuntimeResult;
				try {
					prepared = await this.prepareReplacement(sessionManager, {
						type: "session_start",
						reason: "fork",
						previousSessionFile,
					});
				} catch (error) {
					this.removeCreatedSessionFile(sessionManager.getSessionFile());
					throw error;
				}
				await this.commitReplacement(prepared, {
					reason: "fork",
					targetSessionFile: sessionManager.getSessionFile(),
					withSession: options?.withSession,
					onDiscard: () => this.removeCreatedSessionFile(sessionManager.getSessionFile()),
				});
				return { cancelled: false, selectedText };
			}
			if (!existsSync(currentSessionFile)) {
				throw new Error(
					"This session has not been saved yet. Wait for the first assistant response before cloning or forking it.",
				);
			}

			const sourceSessionManager = SessionManager.open(currentSessionFile, this.services.agentDir, sessionDir);
			const sessionManager = sourceSessionManager.createBranchedSessionManager(targetLeafId);
			const forkedSessionPath = sessionManager.getSessionFile();
			if (!forkedSessionPath) {
				throw new Error("Failed to create forked session");
			}
			let prepared: CreateAgentSessionRuntimeResult;
			try {
				prepared = await this.prepareReplacement(sessionManager, {
					type: "session_start",
					reason: "fork",
					previousSessionFile,
				});
			} catch (error) {
				this.removeCreatedSessionFile(forkedSessionPath);
				throw error;
			}
			await this.commitReplacement(prepared, {
				reason: "fork",
				targetSessionFile: forkedSessionPath,
				withSession: options?.withSession,
				onDiscard: () => this.removeCreatedSessionFile(forkedSessionPath),
			});
			return { cancelled: false, selectedText };
		}

		let sessionManager: SessionManager;
		if (!targetLeafId) {
			sessionManager = SessionManager.inMemory(this.cwd);
			sessionManager.newSession({ parentSession: this.session.sessionFile });
		} else {
			sessionManager = this.session.sessionManager.createBranchedSessionManager(targetLeafId);
		}
		const prepared = await this.prepareReplacement(sessionManager, {
			type: "session_start",
			reason: "fork",
			previousSessionFile,
		});
		await this.commitReplacement(prepared, {
			reason: "fork",
			targetSessionFile: sessionManager.getSessionFile(),
			withSession: options?.withSession,
		});
		return { cancelled: false, selectedText };
	}

	/**
	 * Import a session JSONL file and switch runtime state to the imported session.
	 *
	 * @returns `{ cancelled: true }` when cancelled by `session_before_switch`, otherwise `{ cancelled: false }`.
	 * @throws {SessionImportFileNotFoundError} When the input path does not exist.
	 * @throws {MissingSessionCwdError} When the imported session cwd cannot be resolved and no override is provided.
	 */
	async importFromJsonl(inputPath: string, cwdOverride?: string): Promise<{ cancelled: boolean }> {
		return this.runSessionReplacement(() => this.importFromJsonlUnserialized(inputPath, cwdOverride));
	}

	private async importFromJsonlUnserialized(inputPath: string, cwdOverride?: string): Promise<{ cancelled: boolean }> {
		const resolvedPath = resolvePath(inputPath);
		if (!existsSync(resolvedPath)) {
			throw new SessionImportFileNotFoundError(resolvedPath);
		}

		const sessionDir = this.session.sessionManager.getSessionDir();
		const destinationPath = join(sessionDir, basename(resolvedPath));
		const beforeResult = await this.emitBeforeSwitch("resume", destinationPath);
		if (beforeResult.cancelled) {
			return beforeResult;
		}

		const previousSessionFile = this.session.sessionFile;
		let copied = false;
		if (resolve(destinationPath) !== resolvedPath) {
			if (!existsSync(sessionDir)) mkdirSync(sessionDir, { recursive: true });
			copyFileSync(resolvedPath, destinationPath, fsConstants.COPYFILE_EXCL);
			copied = true;
		}

		let sessionManager: SessionManager;
		let prepared: CreateAgentSessionRuntimeResult;
		try {
			sessionManager = SessionManager.open(destinationPath, this.services.agentDir, sessionDir, cwdOverride);
			assertSessionCwdExists(sessionManager, this.cwd);
			prepared = await this.prepareReplacement(sessionManager, {
				type: "session_start",
				reason: "resume",
				previousSessionFile,
			});
		} catch (error) {
			if (copied) this.removeCreatedSessionFile(destinationPath);
			throw error;
		}
		await this.commitReplacement(prepared, {
			reason: "resume",
			targetSessionFile: sessionManager.getSessionFile(),
			onDiscard: copied ? () => this.removeCreatedSessionFile(destinationPath) : undefined,
		});
		return { cancelled: false };
	}

	async dispose(): Promise<void> {
		if (this._disposeInFlight) return this._disposeInFlight;
		if (this._disposed) return;
		this._disposeStarted = true;
		const replacement = this._replacementInFlight;
		const disposal = (async () => {
			if (replacement) {
				try {
					await replacement;
				} catch {
					// Replacement already reports its own error; dispose whichever runtime it left active.
				}
			}
			const errors = await this.shutdownRuntime(this.session, "quit", undefined, {
				stopResources: true,
				invalidateHost: true,
				progress: this.disposeProgress,
			});
			if (errors.length === 1) throw errors[0];
			if (errors.length > 1) throw new AggregateError(errors, "Session runtime disposal failed.");
			this._disposed = true;
		})();
		this._disposeInFlight = disposal;
		try {
			await disposal;
		} finally {
			this._disposeInFlight = undefined;
		}
	}
}

/**
 * Create the initial runtime from a runtime factory and initial session target.
 *
 * The same factory is stored on the returned AgentSessionRuntime and reused for
 * later /new, /resume, /fork, and import flows.
 */
export async function createAgentSessionRuntime(
	createRuntime: CreateAgentSessionRuntimeFactory,
	options: {
		cwd: string;
		agentDir: string;
		sessionManager: SessionManager;
		sessionStartEvent?: SessionStartEvent;
	},
): Promise<AgentSessionRuntime> {
	assertSessionCwdExists(options.sessionManager, options.cwd);
	const result = await createRuntime(options);
	return new AgentSessionRuntime(
		result.session,
		result.services,
		createRuntime,
		result.diagnostics,
		result.modelFallbackMessage,
	);
}

export {
	type AgentSessionRuntimeDiagnostic,
	type AgentSessionServices,
	type CreateAgentSessionFromServicesOptions,
	type CreateAgentSessionServicesOptions,
	createAgentSessionFromServices,
	createAgentSessionServices,
} from "./agent-session-services.ts";
